import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Pagination from '../../components/common/Pagination';
import { RELEASE_NOTES_QUERY } from '../../graphql/queries';
import styles from './ReleaseNotes.module.css';

const PAGE_SIZE = 30;

interface ReleaseNoteListItem {
  id: string;
  version: string;
  releaseDate: string;
  items: { title: string }[];
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

const ReleaseNotes = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, loading, error } = useQuery(RELEASE_NOTES_QUERY, {
    variables: { page, limit: PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });
  const notes: ReleaseNoteListItem[] = data?.releaseNotes?.notes ?? [];
  const total: number = data?.releaseNotes?.total ?? 0;

  return (
    <DashboardLayout topBarTitle="Release Notes">
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Release Notes</h2>
          <p className="text-on-surface-variant mt-1">What's new in each version of the platform.</p>
        </div>

        {loading && notes.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
            Loading release notes...
          </div>
        ) : error && notes.length === 0 ? (
          <div role="alert" className="p-4 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
            <span className="material-symbols-outlined text-base">error</span>
            Could not load the release notes: {error.message}
          </div>
        ) : notes.length === 0 ? (
          <div className="py-16 text-center bg-white rounded-xl">
            <span className="material-symbols-outlined text-4xl text-outline mb-3 block">campaign</span>
            <p className="text-on-surface-variant">No release notes published yet</p>
          </div>
        ) : (
          <>
          <div className="space-y-4">
            {notes.map((note) => (
              <div key={note.id} className={styles.card}>
                <div className={styles.cardHead}>
                  <button className={styles.versionLink} onClick={() => navigate(`/release-notes/${encodeURIComponent(note.version)}`)}>
                    Version {note.version}
                  </button>
                  <span className={styles.date}>{formatDate(note.releaseDate)}</span>
                  <button
                    className={styles.detailsBtn}
                    onClick={() => navigate(`/release-notes/${encodeURIComponent(note.version)}`)}
                  >
                    Details
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </button>
                </div>
                <ul className={styles.titleList}>
                  {note.items.map((item, i) => (
                    <li key={i} className={styles.titleRow}>
                      <span className="material-symbols-outlined text-base text-primary">check_circle</span>
                      {item.title}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} noun="release notes" variant="card" />
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReleaseNotes;
